import {
  CodeStepExecutionCacheMode,
  DataInput,
  ExecutionType,
  InputSource,
  isProvidedCodeSourceMode,
  TransformCachedCodeReuseScope,
  TransformCodeSourceMode,
  TransformReviewMode,
} from '@/types/automationFields.ts';
import { Alert, Stack, TextareaProps } from '@mantine/core';
import { TransformDataStep, TransformInputContract } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import ExpandableContent from '@/components/ExpandableContent.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import * as React from 'react';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import {
  getMultiLogicDataInputs,
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';
import { STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';
import TransformCodeInputKeys from '@/pages/Editor/components/StepSidebar/StepForms/TransformDataStep/TransformCodeInputKeys.tsx';
import InterventionResponseFields from '@/pages/Editor/components/StepSidebar/StepForms/TransformDataStep/InterventionResponseFields.tsx';
import { useEditingVersionSnapshot } from '@/context/EditVersionContext/useEditVersionContext.ts';
import CodeInputContractFields from '@/pages/Editor/components/StepSidebar/StepForms/CodeInputContractFields.tsx';
import { getConsumerSchemaOptions } from '@/pages/Editor/components/StepSidebar/StepForms/consumerSchemaOptions.ts';

type props = {
  step: TransformDataStep;
  dataInputs: DataInput[];
  updateDataInputs: (dataInputs: DataInput[]) => void;
  hasParentStep?: boolean;
};

type Fields = {
  transformation_logic_source: InputSource;
  transformation_logic_data_inputs: DataInput[];
  transformation_logic_description: string;
  review_instructions: string;
  review_mode: TransformReviewMode;
  execution_type: ExecutionType;
  code_source_mode: TransformCodeSourceMode;
  cached_code_reuse_scope: TransformCachedCodeReuseScope;
  step_execution_cache_mode?: CodeStepExecutionCacheMode;
  provided_code: string;
  input_contracts?: TransformInputContract[];
};

const CODE_SOURCE_OPTIONS = [
  { value: TransformCodeSourceMode.GenerateAlways, label: 'Generate every run' },
  { value: TransformCodeSourceMode.ReuseCachedCode, label: 'Reuse generated code' },
  { value: TransformCodeSourceMode.ProvidedCode, label: 'Use provided code' },
  {
    value: TransformCodeSourceMode.ProvidedCodeWithGeneratedFallback,
    label: 'Try provided, then generate',
  },
];

const CACHED_CODE_REUSE_SCOPE_OPTIONS = [
  { value: TransformCachedCodeReuseScope.WithinParentScope, label: 'Within this loop/run scope' },
  { value: TransformCachedCodeReuseScope.AcrossRuns, label: 'Across runs for this step' },
];

const STEP_EXECUTION_CACHE_OPTIONS = [
  { value: CodeStepExecutionCacheMode.Automatic, label: 'Automatic' },
  { value: CodeStepExecutionCacheMode.AlwaysFresh, label: 'Always run fresh' },
];

const REVIEW_MODE_OPTIONS = [
  { value: TransformReviewMode.Semantic, label: 'Semantic review' },
  {
    value: TransformReviewMode.SchemaAndReviewInstructions,
    label: 'Schema and review instructions',
  },
  { value: TransformReviewMode.SchemaOnly, label: 'Schema only' },
];

type ExpandableTextAreaProps = TextareaProps & {
  collapsedMaxHeight: number;
};

const ExpandableStyledTextArea = ({ collapsedMaxHeight, ...props }: ExpandableTextAreaProps) => {
  return (
    <ExpandableContent maxHeight={collapsedMaxHeight}>
      <StyledTextArea {...props} autosize={true} />
    </ExpandableContent>
  );
};

const TransformDataFields = ({
  step,
  dataInputs,
  updateDataInputs,
  hasParentStep = false,
}: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step });

  const emptyLogicDescription = useStepSpecificError(step, StepErrorType.EmptyDescription);

  const invalidLogicDataInput = useStepSpecificError(step, StepErrorType.InvalidLogicDataInput);
  const invalidProvidedCode = useStepSpecificError(step, StepErrorType.InvalidProvidedCode);

  const executionType = fields.execution_type ?? ExecutionType.AI;
  const codeSourceMode = fields.code_source_mode ?? TransformCodeSourceMode.GenerateAlways;
  const editingVersion = useEditingVersionSnapshot();
  const schemaOptions = getConsumerSchemaOptions(editingVersion);
  const inputContracts = fields.input_contracts ?? step.input_contracts ?? [];
  const codeInputs = dataInputs.filter((input) => !!input.binding_name);

  return (
    <Stack w={'100%'}>
      <InputSourceSelectField
        label="Transformation Logic Source"
        value={step.transformation_logic_source}
        onChange={(value) => onChangeNoDelay({ transformation_logic_source: value })}
      />
      {inputSourceAcceptsUserSpecified(fields.transformation_logic_source) && (
        <Stack>
          <ExpandableStyledTextArea
            label="Transformation Logic"
            value={fields.transformation_logic_description}
            placeholder="Transformation logic description"
            onChange={(event) => {
              onChange({ transformation_logic_description: event.currentTarget.value });
            }}
            required={true}
            minRows={2}
            collapsedMaxHeight={180}
            error={emptyLogicDescription}
            description="Describe the transformation. The selected output schema is authoritative; do not redefine its schema ID or version here."
          />
        </Stack>
      )}
      {inputSourceAcceptsDataInput(fields.transformation_logic_source) && (
        <MultiDataInputSelectorUI
          stepId={step.step_id}
          label={'Transformation Logic Data Inputs'}
          error={invalidLogicDataInput}
          dataInputs={getMultiLogicDataInputs(fields.transformation_logic_data_inputs)}
          updateDataInputs={(dataInputs) =>
            onChangeNoDelay({
              transformation_logic_data_inputs: dataInputs,
            })
          }
        />
      )}
      <StyledTextArea
        label="Review Instructions"
        value={fields.review_instructions ?? ''}
        placeholder="Optional criteria for what the reviewer should check, accept, reject, or ignore."
        onChange={(event) => {
          onChange({ review_instructions: event.currentTarget.value });
        }}
        maxLength={STEP_LOGIC_MAX_LENGTH}
        autosize={true}
        minRows={2}
        description="Use reviewer-only semantic criteria. The selected output schema is authoritative; do not redefine its schema ID or version here."
      />
      <StyledSelect
        label="Review Mode"
        placeholder="Select Review Mode"
        data={REVIEW_MODE_OPTIONS}
        value={fields.review_mode ?? TransformReviewMode.Semantic}
        onChange={(value) => {
          if (value) {
            onChangeNoDelay({ review_mode: value as TransformReviewMode });
          }
        }}
        required={true}
        allowDeselect={false}
        clearable={false}
      />
      <StyledSelect
        label="Transformation Type"
        placeholder="Select Transformation Type"
        data={Object.values(ExecutionType).map((type) => ({ value: type, label: type }))}
        value={fields.execution_type}
        onChange={(value) => {
          const nextExecutionType = value as ExecutionType;
          onChangeNoDelay({
            execution_type: nextExecutionType,
            ...(nextExecutionType === ExecutionType.Code
              ? {}
              : { code_source_mode: TransformCodeSourceMode.GenerateAlways }),
          });
        }}
        required={true}
        allowDeselect={false}
        clearable={false}
      />
      {executionType === ExecutionType.Code && (
        <>
          <StyledSelect
            label="Code Source"
            placeholder="Select Code Source"
            data={CODE_SOURCE_OPTIONS}
            value={codeSourceMode}
            onChange={(value) =>
              onChangeNoDelay({
                code_source_mode: value as TransformCodeSourceMode,
              })
            }
            required={true}
            allowDeselect={false}
            clearable={false}
          />
          <StyledSelect
            label="Completed Execution Reuse"
            placeholder="Select Completed Execution Reuse"
            data={STEP_EXECUTION_CACHE_OPTIONS}
            value={fields.step_execution_cache_mode ?? CodeStepExecutionCacheMode.Automatic}
            onChange={(value) =>
              onChangeNoDelay({
                step_execution_cache_mode: value as CodeStepExecutionCacheMode,
              })
            }
            description="Automatic may reuse an equivalent completed output. Choose Always run fresh when this code depends on live APIs, current time, randomness, mutable external state, or external effects."
            required={true}
            allowDeselect={false}
            clearable={false}
          />
          {codeSourceMode === TransformCodeSourceMode.ReuseCachedCode && (
            <>
              <StyledSelect
                label="Reuse Scope"
                placeholder="Select Reuse Scope"
                data={CACHED_CODE_REUSE_SCOPE_OPTIONS}
                value={fields.cached_code_reuse_scope ?? TransformCachedCodeReuseScope.AcrossRuns}
                onChange={(value) =>
                  onChangeNoDelay({
                    cached_code_reuse_scope: value as TransformCachedCodeReuseScope,
                  })
                }
                required={true}
                allowDeselect={false}
                clearable={false}
              />
              {!!fields.provided_code?.trim() && (
                <Alert color="yellow" title="Provided code will not run">
                  This step is configured to reuse accepted generated code. The retained provided
                  code is ignored at runtime. Switch Code Source to “Use provided code” to execute
                  it, or clear it intentionally.
                </Alert>
              )}
            </>
          )}
          {isProvidedCodeSourceMode(codeSourceMode) && (
            <>
              <ExpandableStyledTextArea
                label="Provided Code"
                value={fields.provided_code ?? ''}
                placeholder="def transform_data(inputs):"
                onChange={(event) => {
                  onChange({ provided_code: event.currentTarget.value });
                }}
                required={true}
                minRows={8}
                collapsedMaxHeight={300}
                error={invalidProvidedCode}
              />
              <TransformCodeInputKeys
                dataInputs={dataInputs}
                stepId={step.step_id}
                updateDataInputs={updateDataInputs}
              />
              {codeSourceMode === TransformCodeSourceMode.ProvidedCode && !hasParentStep && (
                <InterventionResponseFields step={step} />
              )}
            </>
          )}
          {codeInputs.length > 0 && (
            <CodeInputContractFields
              bindingNames={codeInputs.flatMap((input) =>
                input.binding_name ? [input.binding_name] : [],
              )}
              contracts={inputContracts}
              schemaOptions={schemaOptions}
              onChange={(input_contracts) => onChangeNoDelay({ input_contracts })}
              title="Input contracts"
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default TransformDataFields;
